import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import styles from './sheet.module.css';

export interface SheetProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** `'sheet'` (default): bottom sheet at every width - filters, system status, evidence.
   * `'resolution'`: bottom sheet on phone widths, a fixed right-side panel from `768px` up, so
   * the resolution flow can sit beside the note on a wider screen instead of covering it. */
  readonly variant?: 'sheet' | 'resolution';
  readonly headerExtra?: ReactNode;
  /** Persistent action area inside the panel, below its scrollable body. */
  readonly footer?: ReactNode;
  readonly labelledById?: string;
  /** A surface which was opened from another sheet (for example, the complete photo from the
   * resolver) must remain above that sheet. */
  readonly layer?: 'default' | 'foreground';
}

let scrollLockCount = 0;
let lockedScrollY = 0;
let savedBodyStyles: Pick<CSSStyleDeclaration, 'position' | 'top' | 'width' | 'overflowY'> | null =
  null;
let savedDocumentOverflow = '';
const openSheets: symbol[] = [];
const sheetElements = new Map<
  symbol,
  { readonly overlay: HTMLElement; readonly panel: HTMLElement }
>();
const managedAccessibility = new Map<
  HTMLElement,
  {
    readonly ariaHidden: string | null;
    readonly inertAttribute: string | null;
    readonly inert: boolean;
  }
>();

function setManagedAccessibility(element: HTMLElement, hidden: boolean): void {
  const saved = managedAccessibility.get(element);
  if (hidden) {
    if (saved === undefined) {
      managedAccessibility.set(element, {
        ariaHidden: element.getAttribute('aria-hidden'),
        inertAttribute: element.getAttribute('inert'),
        inert: element.inert,
      });
    }
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('inert', '');
    element.inert = true;
    return;
  }
  if (saved === undefined) return;
  if (saved.ariaHidden === null) element.removeAttribute('aria-hidden');
  else element.setAttribute('aria-hidden', saved.ariaHidden);
  if (saved.inertAttribute === null) element.removeAttribute('inert');
  else element.setAttribute('inert', saved.inertAttribute);
  element.inert = saved.inert;
  managedAccessibility.delete(element);
}

function updateModalAccessibility(): void {
  const topSheetId = openSheets.at(-1);
  const root = document.getElementById('root');
  if (root instanceof HTMLElement) setManagedAccessibility(root, topSheetId !== undefined);

  for (const sheetId of openSheets) {
    const elements = sheetElements.get(sheetId);
    if (elements === undefined) continue;
    const covered = sheetId !== topSheetId;
    setManagedAccessibility(elements.overlay, covered);
    if (covered) elements.panel.removeAttribute('aria-modal');
    else elements.panel.setAttribute('aria-modal', 'true');
  }
}

function focusableElements(panel: HTMLElement): HTMLElement[] {
  return [
    ...panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ].filter(
    (element) =>
      !element.hidden && !element.inert && element.getAttribute('aria-hidden') !== 'true',
  );
}

function lockDocumentScroll(): void {
  if (scrollLockCount === 0) {
    lockedScrollY = window.scrollY;
    savedBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflowY: document.body.style.overflowY,
    };
    savedDocumentOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${String(lockedScrollY)}px`;
    document.body.style.width = '100%';
    document.body.style.overflowY = 'scroll';
  }
  scrollLockCount += 1;
}

function unlockDocumentScroll(): void {
  scrollLockCount -= 1;
  if (scrollLockCount !== 0 || savedBodyStyles === null) return;

  document.body.style.position = savedBodyStyles.position;
  document.body.style.top = savedBodyStyles.top;
  document.body.style.width = savedBodyStyles.width;
  document.body.style.overflowY = savedBodyStyles.overflowY;
  document.documentElement.style.overflow = savedDocumentOverflow;
  if (lockedScrollY !== 0) window.scrollTo(0, lockedScrollY);
  savedBodyStyles = null;
}

/**
 * The one shared overlay primitive behind every "Filtrar", "Sistema", per-field resolution, and
 * evidence surface in the redesign - a bottom sheet on a phone, closable by the button, `Escape`,
 * or the backdrop, with focus moved to its close control on open and returned to the trigger on
 * close (same contract as `review/FullImageOverlay.tsx`, generalized).
 */
export function Sheet({
  title,
  onClose,
  children,
  variant = 'sheet',
  headerExtra,
  footer,
  labelledById,
  layer = 'default',
}: SheetProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const sheetId = useRef(Symbol('sheet'));
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const currentSheetId = sheetId.current;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    openSheets.push(currentSheetId);
    if (overlayRef.current !== null && panelRef.current !== null) {
      sheetElements.set(currentSheetId, { overlay: overlayRef.current, panel: panelRef.current });
    }
    lockDocumentScroll();
    closeRef.current?.focus();
    updateModalAccessibility();
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && openSheets.at(-1) === currentSheetId) {
        onCloseRef.current();
      }
      if (event.key !== 'Tab' || openSheets.at(-1) !== currentSheetId || panelRef.current === null)
        return;
      const focusable = focusableElements(panelRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panelRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panelRef.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const sheetIndex = openSheets.lastIndexOf(currentSheetId);
      if (sheetIndex !== -1) openSheets.splice(sheetIndex, 1);
      const elements = sheetElements.get(currentSheetId);
      if (elements !== undefined) setManagedAccessibility(elements.overlay, false);
      sheetElements.delete(currentSheetId);
      updateModalAccessibility();
      unlockDocumentScroll();
      previouslyFocused.current?.focus();
    };
  }, []);

  return createPortal(
    <div
      ref={overlayRef}
      className={[
        styles.overlay,
        variant === 'resolution' ? styles.overlayResolution : null,
        layer === 'foreground' ? styles.overlayForeground : null,
      ]
        .filter(Boolean)
        .join(' ')}
      data-layer={layer}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className={[styles.panel, variant === 'resolution' ? styles.panelResolution : null]
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-label={labelledById === undefined ? title : undefined}
        aria-labelledby={labelledById}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.headerRight}>
            {headerExtra}
            <button ref={closeRef} type="button" className={styles.closeButton} onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
        <div className={styles.body}>{children}</div>
        {footer === undefined ? null : <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
